import type {
  MultipleProductCard,
  MultipleProductCardVariant,
  SingularProductCard
} from "@shared/graphql.types";
import type { ProductCardDataType } from "@shared/ui.components";

interface BaseTrackingProperties {
  location_type: string;
  location_type_details?: string;
  result_id?: string;
}

interface ProductTrackingProperties extends BaseTrackingProperties {
  sku: string;
  name: string;
  brand: string;
  variant: string;
  members_price: number;
  typical_price: number;
  url: string;
  currency: string;
}

interface ShadeClickedProperties extends BaseTrackingProperties {
  module_content: string;
}

export const createProductClickedTrackingObject = (
  product: MultipleProductCard | SingularProductCard,
  variant: MultipleProductCardVariant,
  baseProps: BaseTrackingProperties
): ProductTrackingProperties => ({
  location_type: baseProps.location_type,
  location_type_details:
    baseProps.location_type_details || "View More Details Button",
  sku: variant?.sku || "",
  name: product.title,
  brand: product.brandName || "",
  variant: variant.shade || "",
  members_price:
    variant.prices?.member?.regular.raw || variant.pricing.primary.raw || 0,
  typical_price:
    variant.prices?.guest?.regular.raw || variant.pricing.secondary.raw || 0,
  url: variant.href || "",
  currency:
    variant.prices?.member?.regular.currency ||
    variant.pricing.primary.currency ||
    "",
  result_id: baseProps.result_id ?? ""
});

export const createShadeButtonClickedTrackingObject = (
  product: MultipleProductCard,
  variant: MultipleProductCardVariant,
  location: string
): ShadeClickedProperties => ({
  module_content: "Shade Button Clicked",
  location_type: `${location} Shade Button: ${product.brandName} ${product.title} ${variant.shade}`
});

export const createCloseShadeSelectorTrackingObject = (
  locationTypeDetails: string,
  location: string
): ShadeClickedProperties => ({
  module_content: "Close Shade Selector",
  location_type: location,
  location_type_details: `Clicked ${locationTypeDetails}`
});

export const createAddToCartTrackingObject = (
  product: ProductCardDataType,
  location: string
) => ({
  cart_id: product.sku,
  product_id: product.id,
  sku: product.sku,
  name: product.productName,
  brand: product.brandName,
  variant: product.variationName,
  price: product.prices[product.prices.activeType].regular.raw,
  quantity: 1,
  location_type: location
});

export const createProductClickTrackingObject = (
  product: ProductCardDataType,
  context: {
    location: string;
    position?: number;
    resultType?: string;
    resultId?: string;
    searchTerm?: string;
  }
) => ({
  sku: product.sku,
  name: product.productName,
  brand: product.brandName,
  variant: product.variationName || "",
  url: product.href,
  location_type: context.location,
  position: context.position,
  members_price:
    product.prices?.member?.regular.raw || product.primaryPriceRaw || 0,
  typical_price:
    product.prices?.guest?.regular.raw || product.secondaryPriceRaw || 0,
  currency: product.prices?.[product.prices.activeType]?.regular.currency || "",
  ...(context.resultType && { result_type: context.resultType }),
  ...(context.resultId && { result_id: context.resultId }),
  ...(context.searchTerm && { search_term: context.searchTerm })
});
